import React, {FC, useState, useEffect} from "react"

import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import Button from "@irati/ui/src/Button"
import theme from "@irati/ui/src/global/theme"
import adminTracking from "@irati-tracking/admin"
import workosService from "@irati/workos/services/workos"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import authentication from "@irati/auth"

import {Img} from "../styled"
import {SETTINGS_LOGIN_INTEGRATION} from "../../../../../../routes/routeConfig"
import {LaunchTime} from "./LaunchInvitationsModal"
import SetupLink from "./SetupLink"

import AbandonModal from "./AbandonModal"

interface Props extends InjectIntl {
  show: boolean
  launchTime: LaunchTime
  onClose: () => void
}

const GenerateSetupLinkModal: FC<Props> = ({show, launchTime, onClose, formatMessage}) => {
  const [submitLoad, setSubmitLoad] = useState<Promise<void>>()
  const [submitLinkLoad, setSubmitLinkLoad] = useState<Promise<void>>()
  const [generateLink, setGenerateLink] = useState(false)
  const [showModal, setShowModal] = useState(show)
  const [showAbandonModal, setShowAbandonModal] = useState(false)

  const handleOpenLink = () => {
    const load = workosService.generatePortalLinks(authentication.business().uuid).then((portalLinks) => {
      if (portalLinks?.directorySyncUrl) {
        window.location = portalLinks.directorySyncUrl
        trackEvent("work_os_start")
      }
    })
    setSubmitLoad(load)
  }

  const handleOnSubmit = (load) => {
    setSubmitLinkLoad(load)
    setGenerateLink(false)
  }

  const handleGenerateLink = () => {
    trackEvent("generate_link")
    setGenerateLink(true)
  }

  const trackEvent = (action: string) => {
    adminTracking.hrisUpsellSetup("admin_dysnc_set_up_confirm_invite_options", {action})
  }

  const handleOnReturn = () => {
    setShowAbandonModal(false)
    setShowModal(true)
    setGenerateLink(true)
  }

  const handleOnClose = () => {
    setShowAbandonModal(false)
    onClose()
  }

  const handleOnHide = () => {
    setShowModal(false)
    setShowAbandonModal(true)
  }

  useEffect(() => {
    setShowModal(show)
  }, [show])

  const title =
    launchTime === LaunchTime.AfterSetup
      ? "admin.components.integration.directorySync.hris.upsell.generateSetupLink.title1"
      : "admin.components.integration.directorySync.hris.upsell.generateSetupLink.title"

  const subTitle =
    launchTime === LaunchTime.AfterSetup
      ? "admin.components.integration.directorySync.hris.upsell.generateSetupLink.subTitle1"
      : "admin.components.integration.directorySync.hris.upsell.generateSetupLink.subTitle"

  return (
    <>
      <GenericModal customWidth={750} hideActions show={showModal} onHide={handleOnHide}>
        <Flex justifyContent="center" marginBottom={4}>
          <Img width="150px" src={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.generateSetupLink.img"})} />
        </Flex>
        <H2 color={theme.colors.perkbox.greenBlue} marginBottom={3}>
          <FormattedMessage id={title} />
        </H2>
        <Body marginBottom={2}>
          <FormattedMessage id={subTitle} />
        </Body>
        <SetupLink
          show={generateLink}
          title="admin.components.integration.directorySync.hris.upsell.generateSetupLink.generatedLink"
          onSubmit={handleOnSubmit}
        />
        <Flex justifyContent="flex-end" gap={2} marginTop>
          <ButtonRouterLink large to={SETTINGS_LOGIN_INTEGRATION} onClick={() => trackEvent("return_to_integrations")}>
            <Flex justifyContent="center">
              <Icon icon={IconTypes.angleLeft} marginLeft={2} marginTop={theme.spacing.half} />
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.generateSetupLink.button1" />
            </Flex>
          </ButtonRouterLink>
          <Button primary load={submitLinkLoad} onClick={handleGenerateLink}>
            <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.generateSetupLink.button2" />
          </Button>
          <Button primary load={submitLoad} onClick={handleOpenLink}>
            <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.generateSetupLink.button3" />
          </Button>
        </Flex>
      </GenericModal>
      <AbandonModal show={showAbandonModal} onClose={handleOnClose} onReturn={handleOnReturn} />
    </>
  )
}

export default injectIntl(GenerateSetupLinkModal)
